import React, { useEffect, useState } from "react";
import { Select, Table, message } from "antd";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { KeyRound } from "lucide-react";
import {
  AUTH_TOKEN,
  REACT_APP_API,
  ORG_ID,
  DOMAIN_ID,
} from "../../constants";
import { formatTimeByTimezone } from "../../common/formatTimebyTimezone";
import { AN_COLORS } from "../../design-system/tokens";
import {
  CountChip,
  FilterCard,
  FilterField,
  MonoCell,
  PageHeader,
  PageShell,
  RowActionMenu,
  SquareBadge,
  StatusPill,
  TableCard,
  anTableProps,
  tableEmpty,
} from "../../design-system/tableUi";

const UserScreen = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(1);

  const [selectedDatabase, setSelectedDatabase] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [appliedRole, setAppliedRole] = useState(null);

  const [databaseList, setDatabaseList] = useState([]);
  const [userList, setUserList] = useState([]);

  const [userData, setUserData] = useState([]);
  const [notification, contextHolder] = message.useMessage();

  const PAGESIZE = 10;
  const [paginationParams, setPaginationParams] = useState({ current: 1, pageSize: PAGESIZE });

  useEffect(() => {
    fetchFilterLists();
    if (location.state && location.state.databaseFilter) {
      setSelectedUser(location.state.userFilter);
      setSelectedDatabase(location.state.databaseFilter);
    }
  }, [location.state]);

  useEffect(() => {
    fetchUserData();
  }, [selectedDatabase, selectedUser, appliedRole, paginationParams]);

  const fetchFilterLists = async () => {
    try {
      const payload = {
        orgId: ORG_ID(),
        tenantId: DOMAIN_ID(),
      };

      const dbRes = await axios.post(
        `${REACT_APP_API}/api/v1/databaseService/getUniqueDatabasenames`,
        payload,
        {
          headers: { "X-Authorization": AUTH_TOKEN(), withCredentials: true },
        }
      );

      // Fetch unique user names
      const userRes = await axios.post(
        `${REACT_APP_API}/api/v1/databaseService/getUniqueUsernames`,
        payload,
        {
          headers: { "X-Authorization": AUTH_TOKEN(), withCredentials: true },
        }
      );

      setDatabaseList(dbRes.data?.db_names || []);
      setUserList(userRes.data?.usernames || []);
    } catch (error) {
      console.error("Error fetching filter lists:", error?.response?.status, error?.message);
      notification.error({
        content: "Unable to fetch database/user lists",
      });
    }
  };

  const fetchUserData = () => {
    const filters = [];
    if (selectedDatabase)
      filters.push({ filterParameter: "Database", filterValue: selectedDatabase.split(" (")[0] });
    if (selectedUser) filters.push({ filterParameter: "User", filterValue: selectedUser });
    if (appliedRole) filters.push({ filterParameter: "Role", filterValue: appliedRole });

    const payload = {
      orgId: ORG_ID(),
      tenantId: DOMAIN_ID(),
      filters,
      requestId: "",
      limit: paginationParams.pageSize,
      page_id: paginationParams.current,
    };

    setIsLoading(true);

    axios
      .post(`${REACT_APP_API}/api/v1/databaseService/listUser`, payload, {
        headers: { "X-Authorization": AUTH_TOKEN(), withCredentials: true },
      })
      .then((res) => {
        setIsLoading(false);
        setUserData(
          (res?.data?.data || []).map((item) => ({
            ...item,
            id: item.id,
          }))
        );
        setTotalCount(res?.data?.total_count || 0);
      })
      .catch((err) => {
        setIsLoading(false);
        notification.error({ content: "Unable to fetch users" });
        console.error("fetchUserData failed:", err?.response?.status, err?.message);
      });
  };

  const columns = [
    {
      title: "Username",
      dataIndex: "username",
      key: "username",
      render: (text) => (
        <span style={{ fontWeight: 500, color: AN_COLORS.ink }}>{text || "—"}</span>
      ),
    },
    {
      title: "Database",
      dataIndex: "db_name",
      key: "db_name",
      render: (text) => text || "—",
    },
    {
      title: "Host",
      dataIndex: "host",
      key: "host",
      render: (text) => <MonoCell>{text}</MonoCell>,
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      width: 120,
      render: (text) => <SquareBadge tone={text === "Admin" ? "warn" : undefined}>{text}</SquareBadge>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 140,
      render: (text) => <StatusPill status={text} />,
    },
    {
      title: "Created",
      dataIndex: "created_at",
      key: "created_at",
      width: 200,
      render: (text) => (
        <MonoCell>{formatTimeByTimezone(text, localStorage.getItem("timezone"))}</MonoCell>
      ),
    },
    {
      title: "",
      key: "actions",
      width: 56,
      align: "right",
      render: (_, record) => (
        <RowActionMenu
          items={[
            {
              key: "viewPrivileges",
              icon: <KeyRound size={13} />,
              label: "View privileges",
              onClick: () =>
                navigate("/pam/databases/userPrivileges", {
                  state: { userFilter: record.username },
                }),
            },
          ]}
        />
      ),
    },
  ];

  const resetPage = () => setPaginationParams({ current: 1, pageSize: PAGESIZE });

  const appliedFilters = [
    selectedDatabase && {
      key: "Database",
      label: `Database · ${selectedDatabase}`,
      clear: () => {
        setSelectedDatabase(null);
        resetPage();
      },
    },
    selectedUser && {
      key: "User",
      label: `User · ${selectedUser}`,
      clear: () => {
        setSelectedUser(null);
        resetPage();
      },
    },
    appliedRole && {
      key: "Role",
      label: `Role · ${appliedRole}`,
      clear: () => {
        setAppliedRole(null);
        resetPage();
      },
    },
  ].filter(Boolean);

  const clearFilters = () => {
    setSelectedDatabase(null);
    setSelectedUser(null);
    setAppliedRole(null);
    resetPage();
  };

  return (
    <PageShell>
      {contextHolder}

      <PageHeader
        eyebrow="Admin · Database · Users"
        title="Database Users"
        subtitle="Accounts discovered on your databases, with their role and MFA enrollment status."
        chips={<CountChip count={totalCount} label={totalCount === 1 ? "User" : "Users"} />}
      />

      <FilterCard
        columns="1.6fr 1fr 1fr"
        applied={appliedFilters}
        onClearAll={clearFilters}
        showing={userData.length}
        total={totalCount}
      >
        <FilterField label="Database">
          <Select
            showSearch
            allowClear
            placeholder="Any database"
            style={{ width: "100%", height: 36 }}
            value={selectedDatabase}
            onChange={(value) => {
              setSelectedDatabase(value ?? null);
              resetPage();
            }}
            filterOption={(input, option) =>
              (option?.value ?? "").toLowerCase().includes(input.toLowerCase())
            }
            options={databaseList.map((db) => ({ label: db.split(" (")[0], value: db }))}
          />
        </FilterField>

        <FilterField label="User">
          <Select
            showSearch
            allowClear
            placeholder="Any user"
            style={{ width: "100%", height: 36 }}
            value={selectedUser}
            onChange={(value) => {
              setSelectedUser(value ?? null);
              resetPage();
            }}
            filterOption={(input, option) =>
              (option?.value ?? "").toLowerCase().includes(input.toLowerCase())
            }
            options={userList.map((user) => ({ label: user, value: user }))}
          />
        </FilterField>

        <FilterField label="Role">
          <Select
            allowClear
            placeholder="Any role"
            style={{ width: "100%", height: 36 }}
            value={appliedRole}
            onChange={(value) => {
              setAppliedRole(value ?? null);
              resetPage();
            }}
            options={[
              { value: "Admin", label: "Admin" },
              { value: "User", label: "User" },
            ]}
          />
        </FilterField>
      </FilterCard>

      <TableCard>
        <Table
          {...anTableProps}
          columns={columns}
          dataSource={userData}
          loading={isLoading}
          rowKey={(record) => record.id ?? `${record.username}-${record.db_name}-${record.host}`}
          locale={tableEmpty(
            isLoading,
            appliedFilters.length > 0
              ? "No users match your filters."
              : "No database users found yet.",
          )}
          pagination={{
            current: paginationParams.current,
            pageSize: PAGESIZE,
            total: totalCount,
            showSizeChanger: false,
            onChange: (page) => setPaginationParams({ current: page, pageSize: PAGESIZE }),
          }}
        />
      </TableCard>
    </PageShell>
  );
};

export default UserScreen;
